import React, { useEffect, useRef, useState } from "react";
import { createCombat, updateCombat } from "../services/combatsServices";

// ---------------------- Libellés / Helpers UI ----------------------
const RESULT_LABELS = { V: "Victoire", D: "Défaite", N: "Nul", F: "Forfait", H: "Hansoku-make" };

const resultBtnClass = (active, accent /* 'red' | 'white' */) =>
  `px-3 py-2 rounded-lg text-sm font-medium border transition
   ${active
     ? accent === "red"
       ? "bg-red-600 text-white border-red-600 shadow"
       : "bg-slate-800 text-white border-slate-800 shadow"
     : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`;

const PositionBadge = ({ children }) => (
  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
    {children}
  </span>
);

const BeltChip = ({ color /* 'red' | 'white' */ }) =>
  color === "red" ? (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white border border-red-700">
      ROUGE
    </span>
  ) : (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white text-gray-800 border border-gray-300">
      BLANC
    </span>
  );

const CompetitorHeader = ({ pos, nom, prenom, club, belt }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-3 min-w-0">
      <PositionBadge>{pos}</PositionBadge>
      <div className="min-w-0">
        <p className="font-semibold text-sm text-gray-900 truncate">
          {nom} {prenom}
        </p>
        <p className="text-xs text-gray-500 truncate">{club || "\u00A0"}</p>
      </div>
    </div>
    <BeltChip color={belt} />
  </div>
);

// Empêche les handlers globaux de faire perdre le focus (backdrop/hotkeys…)
const stopAll = (e) => {
  e.preventDefault?.();
  e.stopPropagation?.();
};

const oppositeFor = (value) => {
  if (value === "V") return "D";
  if (value === "D") return "V";
  if (value === "F" || value === "H") return "V";
  return null; // N => les deux N
};

const clampNum = (s) => {
  if (s === "" || isNaN(Number(s))) return 0;
  return Math.max(0, parseInt(s, 10));
};

// ---------------------- Composant ----------------------
const CombatTableauModal = ({
  isOpen,
  onClose,
  competiteur1,
  competiteur2,
  onSubmit,
  initialData,
  distributionId,
  competitionId,
  position1,
  position2,
}) => {
  // Résultats
  const [resultat1, setResultat1] = useState("");
  const [resultat2, setResultat2] = useState("");

  // Points en string (permet la frappe "10", "100", etc.)
  const [points1, setPoints1] = useState("0");
  const [points2, setPoints2] = useState("0");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const inputRef1 = useRef(null);

  // Hydrate à l’ouverture
  useEffect(() => {
    if (!isOpen) return;
    setResultat1(initialData?.resultat_competiteur1 || "");
    setResultat2(initialData?.resultat_competiteur2 || "");
    setPoints1(
      initialData?.points_competiteur1 !== undefined
        ? String(initialData.points_competiteur1)
        : "0"
    );
    setPoints2(
      initialData?.points_competiteur2 !== undefined
        ? String(initialData.points_competiteur2)
        : "0"
    );
    setError("");
    // Focus doux sur le premier input points
    setTimeout(() => inputRef1.current?.focus(), 0);
  }, [isOpen, initialData]);

  // ESC pour fermer
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !competiteur1 || !competiteur2) return null;

  // Sélection d’un résultat → complète l’autre côté
  const pickResult = (side, value) => {
    setError("");
    if (value === "N") {
      setResultat1("N");
      setResultat2("N");
      return;
    }
    if (side === 1) {
      setResultat1(value);
      const opp = oppositeFor(value);
      if (opp) setResultat2(opp);
    } else {
      setResultat2(value);
      const opp = oppositeFor(value);
      if (opp) setResultat1(opp);
    }
  };

  const handleSubmit = async () => {
    try {
      setError("");

      // Si 2 compétiteurs présents => demander des résultats cohérents
      if (competiteur1?.id && competiteur2?.id) {
        if (!resultat1 || !resultat2) {
          setError("Sélectionne un résultat pour les deux compétiteurs.");
          return;
        }
      } else {
        // Cas BYE (rare ici, mais on tolère) : on force V côté 1 si rien n’a été choisi
        if (!resultat1) setResultat1("V");
      }

      setSaving(true);

      const payload = {
        competiteur_id1: competiteur1.id,
        competiteur_id2: competiteur2?.id || null, // BYE autorisé
        distribution_id: distributionId,
        competition_id: competitionId,
        resultat_competiteur1: resultat1 || "V", // fallback BYE
        resultat_competiteur2: competiteur2?.id ? (resultat2 || "D") : "",
        points_competiteur1: clampNum(points1),
        points_competiteur2: clampNum(points2),
      };

      if (initialData?.id) {
        await updateCombat(initialData.id, payload);
      } else {
        await createCombat(payload);
      }

      await Promise.resolve(onSubmit?.(payload));
      onClose();
    } catch (e) {
      console.error("Erreur lors de la soumission du combat :", e);
      setError("Une erreur est survenue pendant l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const ResultAndPoints = ({
    side,
    value,
    points,
    setPoints,
    competitor,
    position,
    belt, // 'red' | 'white'
    inputRef,
    disabled = false, // pour BYE éventuel
  }) => (
    <div
      className={`rounded-2xl border bg-white p-4 shadow-sm ${
        belt === "red" ? "border-red-200" : "border-gray-200"
      }`}
    >
      <div
        className={`rounded-xl p-3 mb-4 border ${
          belt === "red" ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
        }`}
      >
        <CompetitorHeader
          pos={position}
          nom={competitor.nom_competiteur || "Exempt"}
          prenom={competitor.prenom_competiteur || ""}
          club={competitor.club_competiteur || ""}
          belt={belt}
        />
      </div>

      {disabled ? (
        <div className="text-xs text-gray-600">
          Exempt : ce compétiteur ne combat pas à ce tour.
        </div>
      ) : (
        <>
          <div className="mt-2">
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
              Résultat
            </p>
            <div className="grid grid-cols-5 gap-2">
              {["V", "D", "N", "F", "H"].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    pickResult(side, opt);
                  }}
                  className={resultBtnClass(
                    (side === 1 ? resultat1 : resultat2) === opt,
                    belt === "red" ? "red" : "white"
                  )}
                  title={RESULT_LABELS[opt]}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
              Points
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPoints((p) => String(Math.max(0, clampNum(p) - 1)));
                }}
                className="px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                aria-label="Retirer 1 point"
              >
                −
              </button>

              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={points}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*$/.test(v)) setPoints(v);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.stopPropagation();
                }}
                className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                aria-label={`Points ${belt === "red" ? "rouge" : "blanc"}`}
              />

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPoints((p) => String(clampNum(p) + 1));
                }}
                className="px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                aria-label="Ajouter 1 point"
              >
                +
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const isBye = !competiteur2?.id; // sécurité si jamais modal ouverte avec BYE

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" aria-modal="true" role="dialog">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div
        className="relative z-10 w-[min(980px,92vw)] rounded-3xl bg-white shadow-2xl border border-gray-200"
        onClick={stopAll}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900">Résultats du combat</h2>
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-red-600">Rouge</span> {position1} • {competiteur1.nom_competiteur} {competiteur1.prenom_competiteur}
              {"  "}vs{"  "}
              {isBye ? (
                <span className="font-semibold text-gray-800">Exempt</span>
              ) : (
                <>
                  <span className="font-semibold text-gray-800">Blanc</span> {position2} • {competiteur2.nom_competiteur} {competiteur2.prenom_competiteur}
                </>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className={`grid ${isBye ? "grid-cols-1" : "md:grid-cols-2"} gap-4`}>
            <ResultAndPoints
              side={1}
              value={resultat1}
              points={points1}
              setPoints={setPoints1}
              competitor={competiteur1}
              position={position1}
              belt="red"
              inputRef={inputRef1}
            />
            {isBye ? (
              <div className="rounded-2xl border bg-white p-4 shadow-sm border-gray-200">
                <div className="rounded-xl p-3 mb-2 border bg-gray-50 border-gray-200">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <PositionBadge>{position2 || "-"}</PositionBadge>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-gray-900 truncate">Exempt</p>
                        <p className="text-xs text-gray-500 truncate">&nbsp;</p>
                      </div>
                    </div>
                    <BeltChip color="white" />
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  Aucun adversaire : le judoka <span className="font-semibold">ROUGE</span> est automatiquement qualifié.
                </p>
              </div>
            ) : (
              <ResultAndPoints
                side={2}
                value={resultat2}
                points={points2}
                setPoints={setPoints2}
                competitor={competiteur2}
                position={position2}
                belt="white"
              />
            )}
          </div>

          {error && (
            <div className="mt-5 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-white font-medium shadow-sm ${
              saving ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CombatTableauModal;
